import { isNative } from '../platform';
import { Preferences } from '@capacitor/preferences';

const BIOMETRIC_SESSION_KEY = 'biometric_session';
const BIOMETRIC_ENROLLED_KEY = 'biometric_enrolled';

interface NativeBiometricPlugin {
  isAvailable(): Promise<{ isAvailable: boolean; biometryType?: number }>;
  verifyIdentity(options: {
    reason: string;
    title: string;
    negativeButtonText?: string;
  }): Promise<void>;
}

async function getBiometricPlugin(): Promise<NativeBiometricPlugin | null> {
  if (!isNative) return null;
  try {
    const mod = await import('capacitor-native-biometric' as never) as unknown as {
      NativeBiometric: NativeBiometricPlugin;
    };
    return mod.NativeBiometric ?? null;
  } catch {
    return null;
  }
}

/** Returns true if the device supports and has biometrics enrolled. */
export async function isBiometricAvailable(): Promise<boolean> {
  const plugin = await getBiometricPlugin();
  if (!plugin) return false;
  try {
    const { isAvailable } = await plugin.isAvailable();
    return isAvailable;
  } catch {
    return false;
  }
}

/** Returns true if the user has previously opted into biometric login. */
export async function isBiometricEnrolled(): Promise<boolean> {
  if (!isNative) return false;
  const { value } = await Preferences.get({ key: BIOMETRIC_ENROLLED_KEY });
  return value === 'true';
}

/** Saves a session token to use after biometric verification. */
export async function saveSessionForBiometric(sessionToken: string): Promise<void> {
  if (!isNative) return;
  await Preferences.set({ key: BIOMETRIC_SESSION_KEY,  value: sessionToken });
  await Preferences.set({ key: BIOMETRIC_ENROLLED_KEY, value: 'true' });
}

/** Removes biometric enrollment. */
export async function clearBiometricSession(): Promise<void> {
  if (!isNative) return;
  await Preferences.remove({ key: BIOMETRIC_SESSION_KEY });
  await Preferences.remove({ key: BIOMETRIC_ENROLLED_KEY });
}

/**
 * Prompts biometric verification, then returns the stored session token.
 * Returns null if cancelled, failed, or not enrolled.
 */
export async function loginWithBiometric(): Promise<string | null> {
  const plugin = await getBiometricPlugin();
  if (!plugin) return null;
  try {
    await plugin.verifyIdentity({
      reason:             'Confirme sua identidade para entrar no Professya',
      title:              'Professya',
      negativeButtonText: 'Cancelar',
    });
    const { value } = await Preferences.get({ key: BIOMETRIC_SESSION_KEY });
    return value ?? null;
  } catch {
    return null; // cancelled or failed, fall through to password login
  }
}
